import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, User, Lock, Eye, EyeOff, Wallet, TrendingUp, BarChart3, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api, { agencySettingsAPI } from '../lib/api';
import AuthLayout from '../components/auth/AuthLayout';
import { toast } from 'sonner';

const OWNER_ACCENT = '#D4A745';  // Gold

const OwnerLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agencySettings, setAgencySettings] = useState(null);

  useEffect(() => {
    agencySettingsAPI.getPublic().then(r => setAgencySettings(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) return toast.error('Veuillez remplir tous les champs');
    setLoading(true);
    try {
      const res = await api.post('/owner/login', form);
      localStorage.setItem('owner_token', res.data.access_token);
      localStorage.setItem('owner_data', JSON.stringify(res.data.owner));
      localStorage.setItem('owner_properties', JSON.stringify(res.data.properties));
      toast.success(`Bienvenue, ${res.data.owner.name}`);
      navigate('/owner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const logo = agencySettings?.logo_url && agencySettings.logo_url.length > 0
    ? (agencySettings.logo_url.startsWith('http')
        ? agencySettings.logo_url
        : `${process.env.REACT_APP_BACKEND_URL}${agencySettings.logo_url}`)
    : null;

  return (
    <AuthLayout
      accentColor={OWNER_ACCENT}
      accentLabel="Portail Proprietaire"
      title="Vos biens, votre vision financiere"
      tagline="Suivez en temps reel les loyers percus, vos soldes disponibles, les frais et generez vos releves PDF a tout moment."
      benefits={[
        { icon: Wallet, label: 'Soldes par bien et bilan global toujours a jour' },
        { icon: TrendingUp, label: 'Demande de retrait Mobile Money en un clic' },
        { icon: BarChart3, label: 'Releves financiers PDF detailles, exportables' },
      ]}
      agencyLogoUrl={logo}
      agencyName={agencySettings?.name || "GAB'S SERVICES HABITAT"}
      otherPortals={[
        { to: '/login', label: 'Espace Agent', sub: 'Admin / Personnel' },
        { to: '/tenant/login', label: 'Espace Locataire', sub: 'Quittances, paiements...' },
      ]}
    >
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-[#D4A745] mb-2">
          <Building2 className="w-3.5 h-3.5" /> Proprietaire
        </div>
        <h2 className="text-2xl font-bold text-[#1B3A4B]">Bienvenue dans votre espace</h2>
        <p className="text-sm text-gray-500 mt-1">Connectez-vous avec vos identifiants proprietaire</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="owner-login-form">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Identifiant</Label>
          <div className="relative group">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-[#D4A745] transition-colors" />
            <Input
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              placeholder="Votre identifiant"
              className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-[#D4A745] focus:ring-1 focus:ring-[#D4A745]"
              data-testid="owner-username"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Mot de passe</Label>
          <div className="relative group">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-[#D4A745] transition-colors" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              className="pl-11 pr-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-[#D4A745] focus:ring-1 focus:ring-[#D4A745]"
              data-testid="owner-password"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1B3A4B] transition-colors" tabIndex={-1}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#1B3A4B] hover:bg-[#142D3A] text-white font-semibold relative overflow-hidden group"
          data-testid="owner-login-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Connexion...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              Acceder a mon espace
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          )}
          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4A745]" />
        </Button>
      </form>

      <p className="mt-6 text-xs text-center text-gray-500">
        Vous n'avez pas vos identifiants ? Contactez votre agence pour les obtenir.
      </p>
    </AuthLayout>
  );
};

export default OwnerLogin;
